import { useApi } from '@/components/Functional/hooks-core/useApi'
import type {
  RecordShareSnsTapRequest,
  ShareSnsContext,
  ShareSnsData,
  ShareSnsItemData,
  ShareSnsType,
} from '@/schemas/v2/share-sns'
import { useTokenStore } from '@/store/useTokenStore'
import { IconBrandFacebook, IconBrandInstagram, IconBrandTiktok, IconBrandX } from '@tabler/icons-react'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  shareSns: ShareSnsData
  /** タップ記録先スポットID（POST /spots/{id}/share-sns/taps） */
  spotId: number
  /** 拡散の発火点（stamp=スタンプ獲得 / ar_photo=ARフォト） */
  context: ShareSnsContext
  /** 獲得したスタンプID（context=stamp のタップ記録に必須） */
  stampId?: number | null
}

const SNS_META: Record<ShareSnsType, { label: string; Icon: typeof IconBrandFacebook }> = {
  facebook: { label: 'Facebook', Icon: IconBrandFacebook },
  x: { label: 'X', Icon: IconBrandX },
  instagram: { label: 'Instagram', Icon: IconBrandInstagram },
  tiktok: { label: 'TikTok', Icon: IconBrandTiktok },
}

/**
 * 拡散用SNS（#3285）カード。スタンプ獲得ダイアログ等に表示する。
 * ボタン押下で copyText をクリップボードにコピーし、shareUrl を開き、タップを計測する。
 * デザインは ARフォト撮影後画面（MessageModal のSNSシェアカード）に準拠。
 */
export const ShareSnsCard: FC<Props> = ({ shareSns, spotId, context, stampId }) => {
  const api = useApi()
  const { t } = useTranslation()
  const tokenLoading = useTokenStore((s) => s.loading)
  const idToken = useTokenStore((s) => s.idToken)
  const isAuthenticated = !tokenLoading && idToken !== null

  // TikTok はレスポンスに含まれていても拡散先として表示しない
  const visibleItems = shareSns.items.filter((item) => item.type !== 'tiktok')

  if (!shareSns.enabled || visibleItems.length === 0) return null

  const handleShare = (item: ShareSnsItemData) => {
    // copyText があればクリップボードにコピー（X は shareUrl に含まれるため null）
    if (item.copyText) navigator.clipboard?.writeText(item.copyText).catch(() => {})
    // 拡散先を開く
    window.open(item.shareUrl, '_blank', 'noopener,noreferrer')
    // タップ計測用リクエストボディ（discriminated union: context=stamp は stampId 必須）。
    // context=stamp かつ stampId 欠落は不正な組み立てのため計測をスキップする。
    let body: RecordShareSnsTapRequest
    if (context === 'stamp') {
      if (stampId == null) return
      body = { snsType: item.type, context: 'stamp', stampId }
    } else {
      body = { snsType: item.type, context: 'ar_photo' }
    }
    // タップ計測（失敗しても UX を止めない）。
    // 認証任意: ログイン時は Authorization を送出、未ログインは guest ヘッダー（Bearer none 回避）
    void (isAuthenticated ? api.auth : api.guest).recordShareSnsTap(spotId, body).catch(() => {})
  }

  return (
    <div className="mt-4 mb-4 rounded-[8px] border border-[#FFC500] p-4">
      <p className="mb-4 whitespace-pre-line text-center text-[16px] font-bold leading-snug text-black">
        {t('share_sns.title', 'ハッシュタグをつけて\nSNSに投稿しよう！')}
      </p>

      <div className="flex flex-col items-center gap-3">
        {visibleItems.map((item) => {
          const meta = SNS_META[item.type]
          const Icon = meta.Icon
          return (
            <button
              key={item.type}
              type="button"
              onClick={() => handleShare(item)}
              className="relative flex h-[36px] w-[265px] items-center justify-center rounded-[8px] border border-[#757575] bg-white text-[13px] font-bold text-[#757575]"
            >
              {/* アイコンは左詰め、文言はボタン中央のまま */}
              <Icon size={20} stroke={1.5} className="absolute left-3" />
              {t('share_sns.post_to', '{{sns}}に投稿', { sns: meta.label })}
            </button>
          )
        })}
      </div>

      <p className="mt-4 text-center text-[12px] leading-[1.5] text-[#757575]">
        {t('share_sns.note', '※ボタンを押すとハッシュタグ等がコピーされ、各種SNSアプリが起動します')}
      </p>
    </div>
  )
}
